import { FormProvider, useForm } from "react-hook-form";
import { TRADE_AMOUNT_TYPE, TRADE_TYPE } from "../constants";
import { useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import AmountType from "./AmountType";
import FormHeader from "./FormHeader";
import GoldTotal from "./GoldTotal";
import SideButtons from "./SideButtons";
import SubmitTrade from "./SubmitTrade";
import TradeInput from "./TradeInput";
import TradeRange from "./TradeRange";
import TradeTotal from "./TradeTotal";
import TradeType from "./TradeType";
import { calculateMaximumTradeAmount } from "utils/helpers/trade";
import { tradeSchema } from "utils/validations/tradeSchema";
import { yupResolver } from "@hookform/resolvers/yup";

function TradeForm({
  tradeInitial,
  tradeAmountMax,
  coinMarketDetails,
  setShowMarketDrawer,
}) {
  const { source, destination } = useParams();
  const [searchParams] = useSearchParams();
  const sideId = +searchParams.get("sideId") || 1;
  const walletTypeId = +searchParams.get("walletTypeId") || 1;
  const amountTypeId = +searchParams.get("amountTypeId") || 1;
  const tradeTypeId = +searchParams.get("tradeTypeId") || 1;

  const isPrice = useMemo(() => {
    const selectedTradeType = tradeInitial?.lisTradeType?.find(
      (tradeType) => tradeType?.id === tradeTypeId
    );

    return selectedTradeType?.isPrice;
  }, [tradeInitial?.lisTradeType, tradeTypeId]);

  const defaultValues = {
    coinId: +source,
    coinMasterId: +destination,
    sideId,
    isPrice,
    walletTypeId,
    amountTypeId,
    tradeTypeId,
    amount: null,
    price: null,
  };

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(tradeSchema()),
  });

  const { watch, reset } = methods;
  const coinId = watch("coinId");
  const coinMasterId = watch("coinMasterId");
  const price = watch("price");

  const sourceCoin = useMemo(() => {
    return tradeInitial?.lisCoin?.find((coin) => coin?.id === +source);
  }, [source, tradeInitial?.lisCoin]);

  const destinationCoin = useMemo(() => {
    return tradeInitial?.lisCoin?.find((coin) => coin?.id === +destination);
  }, [destination, tradeInitial?.lisCoin]);

  const coinPairSetting = useMemo(
    () =>
      tradeInitial?.lisCoinPairSetting?.find(
        (setting) =>
          setting?.coinSourceId === coinId &&
          setting?.coinDestinationId === coinMasterId
      ),
    [tradeInitial?.lisCoinPairSetting, coinId, coinMasterId]
  );

  const srcDecimalCount = coinPairSetting?.decimalPlacesCoinSource;
  const destDecimalCount = coinPairSetting?.decimalPlacesCoinDestination;

  const amountPlaceholderCoin = useMemo(() => {
    const isAmount = amountTypeId === TRADE_AMOUNT_TYPE.amount.id;

    if (isAmount) {
      return coinMarketDetails?.coinSourceName;
    }

    return coinMarketDetails?.coinDestinationName;
  }, [amountTypeId, coinMarketDetails]);

  const minimumTradeAmount = useMemo(() => {
    const minSrcValue = coinPairSetting?.minimumTradeAmountCoinSource;
    const minDestValue = coinPairSetting?.minimumTradeAmountCoinDestination;

    if (amountTypeId === TRADE_AMOUNT_TYPE.amount.id) {
      return minSrcValue;
    }

    return minDestValue;
  }, [
    amountTypeId,
    coinPairSetting?.minimumTradeAmountCoinSource,
    coinPairSetting?.minimumTradeAmountCoinDestination,
  ]);

  const maximumTradeAmount = useMemo(
    () =>
      calculateMaximumTradeAmount({
        sideId,
        amountTypeId,
        tradeTypeId,
        tradeAmountMax,
        srcDecimalCount,
        destDecimalCount,
        price,
      }),
    [
      amountTypeId,
      srcDecimalCount,
      destDecimalCount,
      tradeAmountMax,
      price,
      sideId,
      tradeTypeId,
    ]
  );

  useEffect(() => {
    reset((formValues) => ({
      ...formValues,
      price: "",
      amount: "",
      coinId: +source,
      coinMasterId: +destination,
    }));
  }, [tradeTypeId, reset, amountTypeId, source, destination, sideId]);

  return (
    <FormProvider {...methods}>
      <form>
        <FormHeader
          setShowMarketDrawer={setShowMarketDrawer}
          coinMarketDetails={coinMarketDetails}
          sourceCoin={sourceCoin}
        />

        <SideButtons tradeInitial={tradeInitial} />

        <TradeType tradeInitial={tradeInitial} />

        <AmountType tradeInitial={tradeInitial} />

        <TradeInput
          destinationCoin={destinationCoin}
          tradeAmountMax={tradeAmountMax}
          sourceCoin={sourceCoin}
          coinMarketDetails={coinMarketDetails}
          srcDecimalCount={srcDecimalCount}
          destDecimalCount={destDecimalCount}
          amountPlaceholderCoin={amountPlaceholderCoin}
          minimumTradeAmount={minimumTradeAmount}
          maximumTradeAmount={maximumTradeAmount}
        />

        <TradeRange
          tradeAmountMax={tradeAmountMax}
          srcDecimalCount={srcDecimalCount}
          destDecimalCount={destDecimalCount}
        />

        <GoldTotal tradeAmountMax={tradeAmountMax} sourceCoin={sourceCoin} />

        {tradeTypeId !== TRADE_TYPE.market.id && (
          <TradeTotal
            srcDecimalCount={srcDecimalCount}
            destDecimalCount={destDecimalCount}
          />
        )}

        <SubmitTrade
          amountPlaceholderCoin={amountPlaceholderCoin}
          minimumTradeAmount={minimumTradeAmount}
          maximumTradeAmount={maximumTradeAmount}
        />
      </form>
    </FormProvider>
  );
}

export default TradeForm;
